import { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

import cn from '../../../../shared/utils/classNames';
import styles from './Notification.module.css';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationProps {
  id?: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  onClose?: () => void;
  autoClose?: boolean;
}

export const Notification = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
  autoClose = true
}: NotificationProps) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, autoClose, handleClose]);

  const icons = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    info: Info
  };

  const Icon = icons[type];

  if (!isVisible) return null;

  return (
    <div 
      id={id}
      className={cn(
        styles.wrapper,
        styles[type]
      )}
      role="alert"
      aria-live="polite"
    >
      <div className={styles.header}>
        <div className={styles.icon}>
          <Icon size={16} />
        </div>
        <div className={styles.content}>
          {title && (
            <div className={styles.title}>
              {title}
            </div>
          )}
          <div className={styles.message}>
            {message}
          </div>
        </div>
        <button
          onClick={handleClose}
          className={styles.closeButton}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};